import { useEffect, useState } from 'react';
import { getAuth, signInWithCustomToken, Unsubscribe } from "firebase/auth";
import { singletonHook } from 'react-singleton-hook';
import config from '../../config';
import { initializeApp } from 'firebase/app';
import { doc, DocumentReference, DocumentSnapshot, getFirestore, onSnapshot } from 'firebase/firestore';
import { getMiroUserId } from './useDataStoreMiro';
import { DocumentData } from '@mirohq/miro-api/dist/api';
import { getAnalytics, logEvent } from "firebase/analytics";

export type IsProState = boolean | undefined | "error"
interface miroLoginResponse {
    isPro: boolean;
    firebaseToken: string;
    miroUserId: string;
    licenses: { pro: boolean };
}
export const fbApp = initializeApp(config.firebase);
const analytics = getAnalytics(fbApp);
export const fbDb = getFirestore();
export function logAnalytic(eventId: string, eventParams?: { [key: string]: any; }) {
    logEvent(analytics, eventId, eventParams);
}
export let signinStatus: "notStarted" | "started" | "done" = "notStarted";

export const subscribeQueue: {
    docRef: DocumentReference<DocumentData>
    onNext: (snapshot: DocumentSnapshot<any>) => void
    futureUnsub: { u: undefined | Function }
}[] = [];

const _useIsPro = (): {
    isPro: IsProState,
    setIsPro: React.Dispatch<React.SetStateAction<IsProState>> | undefined
} => {
    const [isPro, setIsPro] = useState<IsProState>(undefined);
    useEffect(() => {
        let unsub: Unsubscribe | undefined = undefined;
        const checkIsPro = async () => {
            const responseToLicenseCheck = (data?: boolean) => {

                let receivedIsPro = data
                if (receivedIsPro === undefined) {
                    receivedIsPro = false;
                }
                setIsPro(receivedIsPro);
                window.localStorage.setItem(
                    LicenseLocalStorageKey,
                    JSON.stringify({ pro: receivedIsPro })
                );
            };

            async function licenseCheck() {
                // Check the database for license info
                const sendBody = {
                    // @ts-ignore
                    miroToken: await miro.board.getIdToken(),
                    app: config.id,
                    unverifiedBoardId: (await miro.board.getInfo()).id
                };

                const response: Response = await fetch(
                    config.refractiveApiPrefix + "mirologin",
                    {
                        method: "POST", // *GET, POST, PUT, DELETE, etc.
                        mode: "cors", // no-cors, *cors, same-origin
                        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
                        credentials: "same-origin", // include, *same-origin, omit
                        headers: {
                            "Content-Type": "application/json",
                            // 'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        redirect: "follow", // manual, *follow, error
                        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
                        body: JSON.stringify(sendBody),
                    }
                );

                // check the response and update apriopriately
                const data: miroLoginResponse = await response.json(); // parses JSON response into native JavaScript objects
                responseToLicenseCheck(data?.licenses?.pro);

                // Subscribe if the user exists
                const fbDb = getFirestore(fbApp);
                await signInWithCustomToken(getAuth(), data.firebaseToken);
                unsub = onSnapshot(
                    doc(fbDb, `/miroUsers/${data?.miroUserId}/licenses/`, config.id),
                    (doc) => {
                        if (doc) {
                            responseToLicenseCheck(doc.data()?.pro);
                        }
                    }
                );

                // Run Queues up subscriptions
                signinStatus = "done";
                for (const i of subscribeQueue) {
                    i.futureUnsub.u = onSnapshot(i.docRef, i.onNext)
                }
            }

            //Check Local Storage
            const insecureMiroUserId = await getMiroUserId();
            const LicenseLocalStorageKey = "lic" + insecureMiroUserId;
            const insecureLicenseText = window.localStorage.getItem(
                LicenseLocalStorageKey
            );
            let insecureLicense = { pro: false };
            if (insecureLicenseText) {
                insecureLicense = JSON.parse(insecureLicenseText);
            } else {
                window.localStorage.setItem(
                    LicenseLocalStorageKey,
                    JSON.stringify(insecureLicense)
                );
            }
            setIsPro(insecureLicense.pro);
            if (insecureLicense.pro || config.licenseAutoCheck) {
                await licenseCheck();
            }
        };

        logAnalytic("open");
        checkIsPro();

        // unsubscribe if the component is unmounted
        return () => {
            if (unsub) {
                unsub();
            }
        };
        // only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isPro, setIsPro };
};

export const useIsPro = singletonHook({ isPro: undefined, setIsPro: undefined }, _useIsPro);
